import React, { useState, useEffect } from 'react';
import Paginador from '../UI/Paginador.js';
import LinkAdaptativo from '../UI/LinkAdaptativo.js';
import CuadroDatos from '../UI/CuadroDatos.js';
import MensajeError from '../UI/MensajeError.js';
import BotoneraFiltros from '../UI/BotoneraFiltros.js';
import TablaDatos from '../UI/TablaDatos.js';
import * as UI from '../constantes/UI.js';
import * as Localia from '../constantes/Localia.js';
import * as Data from '../data/DataSeleccionesRivales.js';
import * as Fecha from '../funciones/FuncionesFecha.js';
import styles from './PartidosSeleccionRival.module.css';

const FILTRO_TODOS = 'TODOS';
const FILTRO_OFICIALES = 'OFICIALES';
const FILTRO_AMISTOSOS = 'AMISTOSOS';

const FILTROS = [
  { codigo: FILTRO_TODOS, descripcion: 'Todos' },
  { codigo: FILTRO_OFICIALES, descripcion: 'Oficiales' },
  { codigo: FILTRO_AMISTOSOS, descripcion: 'Amistosos' },
];

const crearTotalesVacios = () => ({
  totalPartidos: 0,
  ganados: 0,
  empatados: 0,
  perdidos: 0,
  golesAFavor: 0,
  golesEnContra: 0,
});

const calcularTotalesFiltrados = (listaPartidos) => {
  const totales = crearTotalesVacios();

  for (const partido of listaPartidos || []) {
    totales.totalPartidos += 1;

    const rlRaw = partido.resultadoLocal;
    const rvRaw = partido.resultadoVisitante;
    if (rlRaw == null || rvRaw == null) {
      continue;
    }

    const rl = Number(rlRaw);
    const rv = Number(rvRaw);
    if (!Number.isFinite(rl) || !Number.isFinite(rv)) {
      continue;
    }

    const esLocal = partido.localia === Localia.LOCAL;
    const golesAFavor = esLocal ? rl : rv;
    const golesEnContra = esLocal ? rv : rl;

    totales.golesAFavor += golesAFavor;
    totales.golesEnContra += golesEnContra;

    if (golesAFavor > golesEnContra) {
      totales.ganados += 1;
    } else if (golesAFavor < golesEnContra) {
      totales.perdidos += 1;
    } else {
      totales.empatados += 1;
    }
  }

  return totales;
};

const esPartidoOficial = (partido) => {
  if (typeof partido?.oficial === 'boolean') {
    return partido.oficial;
  }
  return partido?.motivo !== 'Amistoso';
};

const filtroBackendDesdeFiltro = (filtro) => {
  if (filtro === FILTRO_OFICIALES) {
    return { oficial: true };
  }
  if (filtro === FILTRO_AMISTOSOS) {
    return { oficial: false };
  }
  return null;
};

const filtrarPartidos = (listaPartidos, filtro) => {
  if (filtro === FILTRO_AMISTOSOS) {
    return (listaPartidos || []).filter((partido) => !esPartidoOficial(partido));
  }
  if (filtro === FILTRO_OFICIALES) {
    return (listaPartidos || []).filter((partido) => esPartidoOficial(partido));
  }
  return listaPartidos || [];
};

const getCantidadTotal = async (idPais, tipoRivalId, idRival, categoria, filtro) => {
  const total = await Data.GetCantidadPartidosRival(
    idPais,
    tipoRivalId,
    idRival,
    categoria,
    filtro
  );
  return total == null ? 0 : Number(total);
};

const PartidosSeleccionRival = (props) => {
  const idPais = props.idPais;
  const tipoRivalId = props.tipoRivalId;
  const idRival = props.idRival;
  const categoria = props.categoria;
  const tipoInternacional = props.tipoInternacional;
  const searchTrigger = props.searchTrigger;
  const onTotalesFiltradosChange = props.onTotalesFiltradosChange;
  const [filtro, setFiltro] = useState(FILTRO_TODOS);
  const [partidos, setPartidos] = useState(null);
  const [cantPaginas, setCantPaginas] = useState(null);
  const [errores, setErrores] = useState(null);
  const [isCargando, setIsCargando] = useState(false);
  const [pagina, setPagina] = useState(0);

  useEffect(() => {
    if (searchTrigger === undefined || !idPais || !idRival) return;
    setIsCargando(true);
    setErrores(null);
    setPartidos(null);
    setCantPaginas(null);
    if (typeof onTotalesFiltradosChange === 'function') {
      onTotalesFiltradosChange(undefined);
    }
    const filtroBackend = filtroBackendDesdeFiltro(filtro);

    const cargarPartidos = async () => {
      try {
        let totalPartidos = 0;

        if (tipoRivalId === 'S') {
          const totales = await Data.GetTotalesSeleccionRival(
            idPais,
            idRival,
            categoria,
            filtroBackend,
            tipoInternacional
          );
          totalPartidos = Number(totales?.totalPartidos || 0);
          if (typeof onTotalesFiltradosChange === 'function') {
            onTotalesFiltradosChange(totalPartidos > 0 ? totales : null);
          }
        } else {
          totalPartidos = await getCantidadTotal(
            idPais,
            tipoRivalId,
            idRival,
            categoria,
            filtroBackend
          );
        }

        setCantPaginas(Math.ceil(totalPartidos / UI.PARTIDOS_X_PAGINA));
        const resultado =
          totalPartidos > 0
            ? await Data.GetPartidosRival(
                idPais,
                tipoRivalId,
                idRival,
                categoria,
                pagina,
                UI.PARTIDOS_X_PAGINA,
                filtroBackend,
                tipoInternacional
              )
            : [];
        setPartidos(resultado || []);
      } catch (err) {
        setErrores([err.message || 'Error al obtener partidos']);
        setPartidos(null);
        setCantPaginas(0);
        if (typeof onTotalesFiltradosChange === 'function') {
          onTotalesFiltradosChange(null);
        }
      } finally {
        setIsCargando(false);
      }
    };

    cargarPartidos();
  }, [
    idPais,
    tipoRivalId,
    idRival,
    categoria,
    tipoInternacional,
    searchTrigger,
    filtro,
    pagina,
    onTotalesFiltradosChange,
  ]);

  useEffect(() => {
    setPagina(0);
  }, [filtro]);

  const nombreRival = (partido) =>
    partido.localia === Localia.LOCAL
      ? partido.equipoVisitante?.nombre || '-'
      : partido.equipoLocal?.nombre || '-';

  const condicion = (partido) => (partido.neutral ? Localia.NEUTRAL : partido.localia || '-');

  const resultado = (partido) => {
    const rl = partido.resultadoLocal;
    const rv = partido.resultadoVisitante;
    if (rl == null || rv == null) return '-';
    return partido.localia === Localia.LOCAL ? `${rl} - ${rv}` : `${rv} - ${rl}`;
  };

  const idPartido = (partido) => partido.idPartido ?? partido.id;

  useEffect(() => {
    if (tipoRivalId === 'S') return;
    if (typeof onTotalesFiltradosChange !== 'function' || isCargando) return;
    if (!partidos || partidos.length === 0) {
      onTotalesFiltradosChange(null);
      return;
    }
    onTotalesFiltradosChange(calcularTotalesFiltrados(filtrarPartidos(partidos, filtro)));
  }, [partidos, isCargando, filtro, tipoRivalId, onTotalesFiltradosChange]);

  return (
    <CuadroDatos titulo="Partidos" isCargando={isCargando} errores={errores}>
      <BotoneraFiltros
        filtros={FILTROS}
        selected={filtro}
        onFiltroElegido={setFiltro}
        onResetPagina={setPagina}
      />
      <TablaDatos
        estilo={`${styles.tablaPartidos}`}
        lista={partidos}
        isCargando={isCargando}
        textoVacia="No hay partidos cargados"
      >
        <tbody>
          <tr className={`${styles.titulo}`} key="header">
            <td className={`${styles.fecha}`}>Fecha</td>
            <td className={`${styles.torneo}`}>Torneo</td>
            <td className={`${styles.rival}`}>Rival</td>
            <td className={`${styles.condicion}`}>Cond.</td>
            <td className={`${styles.resultado}`}>Res.</td>
          </tr>
          {partidos &&
            partidos.map((partido) => (
              <tr className={`${styles.partidos}`} key={idPartido(partido)}>
                <td className={`${styles.fecha}`}>
                  {Fecha.CamposToFechaCorta(
                    partido.anioPartido,
                    partido.mesPartido,
                    partido.diaPartido
                  )}
                </td>
                <td className={`${styles.torneo}`}>{partido.motivo}</td>
                <td className={`${styles.rival}`}>{nombreRival(partido)}</td>
                <td className={`${styles.condicion}`}>{condicion(partido)}</td>
                <td className={`${styles.resultado}`}>
                  <LinkAdaptativo to={`/partido/${idPartido(partido)}`} external={true}>
                    {resultado(partido)}
                  </LinkAdaptativo>
                </td>
              </tr>
            ))}
        </tbody>
      </TablaDatos>
      <Paginador
        cantPaginas={cantPaginas}
        cantBotones={5}
        paginaActual={pagina}
        onPaginaElegida={setPagina}
      />
      {errores && <MensajeError errores={errores} />}
    </CuadroDatos>
  );
};

export default PartidosSeleccionRival;
